import React, { useEffect, useMemo, useState } from 'react';
import './MessageComponents.css';
import './MessageReactions.css';
import likeIcon from '../../assets/chaticons/reactions/like.png';
import dislikeIcon from '../../assets/chaticons/reactions/dislike.png';
import heartIcon from '../../assets/chaticons/reactions/heart.png';

const REACTION_MAP = {
  like: { icon: likeIcon, label: 'Like' },
  dislike: { icon: dislikeIcon, label: 'Dislike' },
  heart: { icon: heartIcon, label: 'Heart' },
};

function normalizeReactionType(v) {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function isActiveReaction(reaction) {
  if (reaction?.is_deleted === undefined && reaction?.is_active === undefined) return true;

  const isDeleted = reaction?.is_deleted === true;
  const isActive = reaction?.is_active !== false;
  return !isDeleted && isActive;
}

function safeUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `mock-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function MessageReactions({
  reactions = [],
  allowedReactions = [],
  currentUserNativeId = null,
  currentReactorType = 'user',
  readOnly = false,
  onSetReaction,
  onDeleteReaction,
}) {
  const [local, setLocal] = useState(Array.isArray(reactions) ? reactions : []);
  const [poppingType, setPoppingType] = useState(null);

  useEffect(() => {
    setLocal(Array.isArray(reactions) ? reactions : []);
  }, [reactions]);

  const activeReactions = useMemo(
    () => (Array.isArray(local) ? local.filter(isActiveReaction) : []),
    [local],
  );

  const allowedList = useMemo(() => {
    const base =
      Array.isArray(allowedReactions) && allowedReactions.length > 0
        ? allowedReactions
        : Object.keys(REACTION_MAP);

    const allowed = new Set();
    for (const v of base) {
      const t = normalizeReactionType(v);
      if (REACTION_MAP[t]) allowed.add(t);
    }
    return Object.keys(REACTION_MAP).filter((t) => allowed.has(t));
  }, [allowedReactions]);

  const allowedSet = useMemo(() => new Set(allowedList), [allowedList]);

  const grouped = useMemo(() => {
    const byType = new Map();
    for (const reaction of activeReactions) {
      const reactionType = normalizeReactionType(reaction?.reaction);
      if (!REACTION_MAP[reactionType]) continue;
      const arr = byType.get(reactionType) || [];
      arr.push(reaction);
      byType.set(reactionType, arr);
    }

    return Array.from(byType.entries())
      .map(([type, items]) => ({
        type,
        items,
        count: items.length,
        mine:
          !!currentUserNativeId &&
          items.some((x) => String(x?.reactor_native_id) === String(currentUserNativeId)),
      }))
      .filter((g) => allowedSet.has(g.type));
  }, [activeReactions, currentUserNativeId, allowedSet]);

  const visibleList = useMemo(() => {
    if (!readOnly) return allowedList;
    const reactedTypes = new Set(grouped.map((group) => group.type));
    return allowedList.filter((type) => reactedTypes.has(type));
  }, [readOnly, allowedList, grouped]);

  const toggleReaction = async (type) => {
    if (readOnly) return;

    const reactionType = normalizeReactionType(type);
    if (!REACTION_MAP[reactionType]) return;
    if (!allowedSet.has(reactionType)) return;

    const myId = currentUserNativeId ? String(currentUserNativeId) : null;
    if (!myId) return;

    const hadMine = activeReactions.some(
      (reaction) =>
        normalizeReactionType(reaction?.reaction) === reactionType &&
        String(reaction?.reactor_native_id) === myId,
    );

    const prev = local;

    try {
      if (hadMine) {
        setLocal(
          prev.map((reaction) => {
            if (
              isActiveReaction(reaction) &&
              normalizeReactionType(reaction?.reaction) === reactionType &&
              String(reaction?.reactor_native_id) === myId
            ) {
              return {
                ...reaction,
                is_active: false,
                is_deleted: true,
                updated_at: new Date().toISOString(),
              };
            }
            return reaction;
          }),
        );

        if (typeof onDeleteReaction === 'function') {
          await onDeleteReaction(reactionType);
        }
        return;
      }

      setLocal([
        ...prev,
        {
          key: safeUuid(),
          reaction: reactionType,
          reaction_type: currentReactorType,
          reactor_native_id: myId,
          updated_at: new Date().toISOString(),
          is_active: true,
          is_deleted: false,
        },
      ]);
      setPoppingType(reactionType);

      if (typeof onSetReaction === 'function') {
        await onSetReaction(reactionType);
      }
    } catch (e) {
      setLocal(prev);
      console.error('Reaction API failed:', e);
    }
  };

  const buildTooltip = (group) => {
    const label = REACTION_MAP[group.type]?.label || '';
    if (group.count === 0) return label;
    if (group.count === 1) {
      return group.mine ? `You reacted with ${label}` : label;
    }
    if (group.mine) {
      const others = group.count - 1;
      return others === 1 ? `You and 1 other` : `You and ${others} others`;
    }
    return `${group.count} ${label}`;
  };

  if (visibleList.length === 0) return null;

  return (
    <div className="message-reactions">
      {visibleList.map((type) => {
        const group = grouped.find((g) => g.type === type) || { type, count: 0, mine: false };
        const isPopping = poppingType === type;

        return (
          <button
            key={type}
            type="button"
            className={`reaction-chip ${group.mine ? 'active' : ''} ${group.count === 0 ? 'reaction-chip--empty' : ''} ${readOnly ? 'reaction-chip--readonly' : ''} ${isPopping ? 'reaction-chip--pop' : ''}`}
            onClick={readOnly ? undefined : () => toggleReaction(type)}
            onAnimationEnd={() => {
              if (isPopping) setPoppingType(null);
            }}
            title={REACTION_MAP[type]?.label}
            aria-label={REACTION_MAP[type]?.label}
            aria-pressed={group.mine}
            aria-disabled={readOnly}
          >
            <img
              className="reaction-icon"
              src={REACTION_MAP[type]?.icon}
              alt=""
              aria-hidden="true"
            />
            {group.count > 0 && <span className="reaction-count">{group.count}</span>}
            <span className="reaction-tooltip" role="tooltip">
              {buildTooltip(group)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
